import classNames from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';
import { RoutePath, routeAccess } from 'src/router';

import { useSelector } from 'src/store';
import {
  isCompanyEmployersPayrollRequiredSelector,
  isPayrollReferenceMigrationAvailableSelector,
  selectedCompanyIdSelector,
} from 'src/store/selectors/companySelector';
import {
  payrollEmployeeIdSelector,
  payrollMemberActionSelector,
  payrollRefSelector,
} from 'src/store/selectors/payrollSelector';

import {
  isLoggedInSelector,
  authUserIdSelector,
  authRoleSelector,
  authRoleListSelector,
} from 'src/store/selectors/authSelector';
import {
  tenantBrandingLogoUrlSelector,
  tenantBrandingLogoAltSelector,
  tenantBrandingNameSelector,
} from 'src/store/selectors/tenantBrandingSelector';

import { MenuIcon, PenscopeIcon, HideIcon } from '@itm/shared-frontend/lib/components/icons';
import { LinkWithParams, Navigation, NavigationLocalConfig } from '@itm/shared-frontend/lib/components';

import Filters from 'src/components/Filters';

import { clientPortalDashboardUrl, clientPortalDomain, isAzureTargetPlatform } from 'src/utils/constants';

import { getUserPermissionsByCompany } from 'src/api/clientPortal/permissions';

import { Role, GlobalRole, UserPermissionResponse } from 'src/types';

import styles from './Sidebar.module.scss';

type Props = Readonly<{
  isActive: boolean;
  setIsActive: React.Dispatch<React.SetStateAction<boolean>>;
}>;

function Sidebar({ isActive, setIsActive }: Props) {
  const isLoggedIn = useSelector(isLoggedInSelector);
  const userId = useSelector(authUserIdSelector);
  const userRole = useSelector(authRoleSelector);
  const userRoleListFromToken = useSelector(authRoleListSelector);
  const selectedCompanyId = useSelector(selectedCompanyIdSelector);

  const [userPermissionList, setUserPermissionList] = useState<UserPermissionResponse[]>([]);

  const userRoleList = useMemo<GlobalRole[]>(() => {
    if (!userRole) return [];
    if (userRole === Role.SuperAdmin || userRole === Role.Support) return [userRole] as unknown as GlobalRole[];

    const clientPortalRole = userRoleListFromToken.find((role) => role.startsWith('ClientPortal'));
    const productRolesByCompany = [...new Set(userPermissionList.map(({ productRoleName }) => productRoleName))];

    return clientPortalRole ? [clientPortalRole, ...productRolesByCompany] : productRolesByCompany;
  }, [userPermissionList, userRole, userRoleListFromToken]);

  const fetchUserPermissionList = useCallback(async () => {
    if (!userId || !selectedCompanyId || userRole === Role.SuperAdmin || userRole === Role.Support) {
      setUserPermissionList([]);
      return;
    }
    try {
      const res = await getUserPermissionsByCompany(userId, selectedCompanyId);
      setUserPermissionList(res.data);
    } catch {
      console.warn('User Permissions unavailable');
    }
  }, [userId, selectedCompanyId, userRole]);

  const brandingLogoUrl = useSelector(tenantBrandingLogoUrlSelector);
  const brandingLogoAlt = useSelector(tenantBrandingLogoAltSelector);
  const brandingName = useSelector(tenantBrandingNameSelector);

  const isCompanyEmployersPayrollRequired = useSelector(isCompanyEmployersPayrollRequiredSelector);
  const isPayrollReferenceMigrationAvailable = useSelector(isPayrollReferenceMigrationAvailableSelector);
  const payrollRef = useSelector(payrollRefSelector);
  const employeeId = useSelector(payrollEmployeeIdSelector);
  const action = useSelector(payrollMemberActionSelector);

  const navigationLocalConfig = useMemo<NavigationLocalConfig<Role>>(
    () => [
      {
        name: 'All Products',
        section: 'products',
        path: clientPortalDashboardUrl,
        icon: () => <MenuIcon />,
      },
      {
        name: 'Employer Hub',
        section: 'products',
        path: RoutePath.root,
        isInternalRoute: true,
        icon: () => <PenscopeIcon />,
        children: [
          {
            section: 'common',
            name: 'Configuration',
            path: RoutePath.configurationRoot,
            isInternalRoute: true,
            role: {
              allowList: routeAccess.configurationRoot,
            },
            children: [
              {
                name: 'Template Definitions',
                path: RoutePath.configurationTemplateDefinitionRoot,
                role: { allowList: routeAccess.configurationTemplateDefinitionRoot },
              },
              {
                name: 'Member Request Settings',
                path: RoutePath.configurationMemberRequestSettings,
                role: { allowList: routeAccess.configurationMemberRequestSettings },
              },
              {
                name: 'Admin Synchronisation',
                path: RoutePath.configurationAdminSyncRoot,
                role: { allowList: routeAccess.configurationAdminSyncRoot },
              },
              {
                name: 'All Validations',
                path: RoutePath.configurationValidationsRoot,
                role: { allowList: routeAccess.configurationValidationsRoot },
              },
              {
                name: isCompanyEmployersPayrollRequired ? 'All Payrolls' : null,
                path: RoutePath.configurationPayrollsList,
                role: { allowList: routeAccess.configurationPayrollsList },
              },
              {
                name: isCompanyEmployersPayrollRequired ? 'Add New Payroll' : null,
                path: RoutePath.configurationPayrollsAddRoot,
                role: { allowList: routeAccess.configurationPayrollsAddRoot },
              },
            ],
          },
          {
            section: 'common',
            name: 'Data Management',
            path: RoutePath.dataManagementRoot,
            isInternalRoute: true,
            role: { allowList: routeAccess.dataManagementRoot },
            children: [
              {
                name: 'Data Upload History',
                path: RoutePath.dataManagementUploadHistoryList,
              },
              {
                name: isPayrollReferenceMigrationAvailable ? 'Data Amendments' : null,
                path: RoutePath.dataManagementDataAmendmentsRoot,
                role: { allowList: routeAccess.dataManagementDataAmendmentsRoot },
                children: [
                  {
                    name: 'Data Upload',
                    path: RoutePath.dataManagementDataAmendmentsUpload,
                  },
                  {
                    name: 'Data History',
                    path: RoutePath.dataManagementDataAmendmentsHistory,
                  },
                ],
              },
              {
                name: 'Validation Helper',
                path: RoutePath.dataManagementValidationHelperList,
              },

              {
                name: 'Member Search',
                path: RoutePath.dataManagementMemberList,
              },
              {
                name: payrollRef || null,
                path: generatePath(RoutePath.dataManagementMemberViewEditRoot, {
                  employeeId,
                  action,
                }),
                children: [
                  {
                    name: 'Membership Details',
                    path: generatePath(RoutePath.dataManagementMemberViewEditMembershipDetails, {
                      employeeId,
                      action,
                    }),
                  },
                  {
                    name: 'Employment Details',
                    path: generatePath(RoutePath.dataManagementMemberViewEditEmploymentDetails, {
                      employeeId,
                      action,
                    }),
                  },
                  {
                    name: 'Contributions',
                    path: generatePath(RoutePath.dataManagementMemberViewEditContributions, {
                      employeeId,
                      action,
                    }),
                  },
                  {
                    name: 'Audit',
                    path: generatePath(RoutePath.dataManagementMemberViewEditAuditRoot, {
                      employeeId,
                      action,
                    }),
                  },
                ],
              },
              {
                name: 'Scheme Audit',
                path: RoutePath.dataManagementSchemeAuditRoot,
              },
            ],
          },
          {
            section: 'common',
            name: 'Reporting',
            path: RoutePath.reportingRoot,
            isInternalRoute: true,
            role: { allowList: routeAccess.reportingRoot },
            children: [
              {
                name: 'Configure Report',
                path: RoutePath.reportingReportsRoot,
                role: { allowList: routeAccess.reportingReportsRoot },
              },
              {
                name: 'Report History',
                path: RoutePath.reportingHistoryRoot,
                role: { allowList: routeAccess.reportingHistoryRoot },
              },
              {
                name: 'Late Payment Report',
                path: RoutePath.reportingLatePaymentReportRoot,
                role: { allowList: routeAccess.reportingLatePaymentReportRoot },
              },
              {
                name: 'Member Request Details',
                path: RoutePath.reportingRequestsList,
                role: { allowList: routeAccess.reportingRequestsList },
              },
              {
                name: isCompanyEmployersPayrollRequired ? 'Payroll Summary Report' : null,
                path: RoutePath.reportingPayrollSummaryReportRoot,
                role: { allowList: routeAccess.reportingPayrollSummaryReportRoot },
              },
              {
                name: 'Validation Insights',
                path: RoutePath.reportingValidationInsightsRoot,
                role: { allowList: routeAccess.reportingValidationInsightsRoot },
              },
            ],
          },
          {
            name: 'Product Information',
            path: RoutePath.productInfo,
            role: { allowList: routeAccess.productInfo },
          },
        ],
      },
    ],
    [action, employeeId, isCompanyEmployersPayrollRequired, isPayrollReferenceMigrationAvailable, payrollRef],
  );

  useEffect(() => {
    fetchUserPermissionList();
  }, [fetchUserPermissionList]);

  return (
    <aside
      className={classNames([
        styles.Wrapper,
        'is-flex is-flex-direction-column has-background-white is-relative',
        { [styles.IsActive]: isActive },
      ])}
      id="sidebar"
    >
      <header className={classNames(styles.Header, 'is-flex-shrink-0')}>
        <div className="columns is-align-items-center is-gapless mb-0">
          <div className="column is-narrow">
            <LinkWithParams className={classNames(styles.Logo, 'has-icon')} to={RoutePath.root}>
              <img src={brandingLogoUrl} alt={brandingLogoAlt} />
            </LinkWithParams>
          </div>
          {isActive && (
            <div className="column">
              <h2 className="title is-nowrap is-4 ml-4">{brandingName}</h2>
            </div>
          )}
        </div>
        {isActive && <Filters />}
      </header>
      {isLoggedIn && (
        <>
          <button
            className={classNames(styles.ToggleButton, 'has-icon', { 'is-active': isActive })}
            type="button"
            onClick={() => setIsActive(!isActive)}
            aria-controls="sidebar"
            aria-label={isActive ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <span className="icon">
              <HideIcon />
            </span>
          </button>
          {userRole && (
            <Navigation
              isActiveSidebar={isActive}
              clientPortalDomain={clientPortalDomain}
              localConfig={navigationLocalConfig}
              userRoleList={userRoleList}
              isAzureTargetPlatform={isAzureTargetPlatform}
              isCommonItemsCollapse
            />
          )}
        </>
      )}
    </aside>
  );
}

export default Sidebar;
